import XLSX from 'xlsx-js-style';
import { useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import {
  Box,
  Button,
  Paper,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import trLocale from 'date-fns/locale/tr';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import BarChartIcon from '@mui/icons-material/BarChart';
import { PaginatedResponse } from '../../interfaces/PaginatedResponse';
import { Period, periodToDateFormatMap } from '../../interfaces/Period';
import { ProductSalesReport } from '../../interfaces/Report/ProductSalesReport/ProductSalesReport';
import {
  getDefaultProductSalesReportFilter,
  ProductSalesReportFilter,
} from '../../interfaces/Report/ProductSalesReport/ProductSalesReportFilter';
import { getProductSalesReport } from '../../api/Report';
import { Pagination } from '../../interfaces/Pagination';
import { sharedStyles } from '../../utils/Style';
import { Waiting } from '../../components/Waiting/Waiting';
import { dateTimePickerStyle } from '../SalesReport/SalesReportPage';
import { PeriodDropdown } from '../../components/Dropdowns/PeriodDropdown';
import { ProductSalesReportTable } from './ProductSalesReportTable';
import { BrandDropdown } from '../../components/Dropdowns/BrandDropdown';
import { ProductsDropdown } from '../../components/Dropdowns/ProductsDropdown';
import { PartnerPlatformDropdown } from '../../components/Dropdowns/PartnerPlatformDropdown';
import { formatLocalTime } from '../../utils/Date';
import { partnerPlatformNameMap } from '../../constants/PartnerPlatform';
import { ProductSalesChart } from './ProductSalesChart';

export const ProductSalesReportPage = () => {
  const [report, setReport] = useState<PaginatedResponse<ProductSalesReport>>({
    data: [],
    totalCount: 0,
  });
  const [filter, setFilter] = useState(getDefaultProductSalesReportFilter());
  const [appliedPeriod, setAppliedPeriod] = useState<Period | undefined>(
    undefined,
  );
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState<'table' | 'chart'>('table');

  const refreshReport = (filters?: ProductSalesReportFilter) => {
    const appliedFilters = filters ?? filter;

    setLoading(true);
    getProductSalesReport(appliedFilters)
      .then((r) => {
        setReport(r);
        setAppliedPeriod(appliedFilters.period);
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  const downloadReport = async () => {
    setLoading(true);
    try {
      const rows = await getProductSalesReport({
        ...filter,
        pagination: undefined,
      });

      const headers = ['Tarih', 'Marka', 'Ürün', 'Varyant', 'Platform', 'Adet'];

      const headerStyle = {
        font: { bold: true, color: { rgb: 'FFFFFF' } },
        fill: { fgColor: { rgb: '4472C4' } }, // blue background
        alignment: { horizontal: 'center' },
        border: {
          top: { style: 'thin', color: { rgb: '000000' } },
          bottom: { style: 'thin', color: { rgb: '000000' } },
          left: { style: 'thin', color: { rgb: '000000' } },
          right: { style: 'thin', color: { rgb: '000000' } },
        },
      };

      const cellStyle = {
        border: {
          top: { style: 'thin', color: { rgb: 'CCCCCC' } },
          bottom: { style: 'thin', color: { rgb: 'CCCCCC' } },
          left: { style: 'thin', color: { rgb: 'CCCCCC' } },
          right: { style: 'thin', color: { rgb: 'CCCCCC' } },
        },
      };

      const data = rows.data.map((r) => [
        formatLocalTime(
          r.createdAt,
          filter.period ? periodToDateFormatMap[filter.period] : undefined,
        ),
        r.brandName ?? '',
        r.productName,
        r.optionValueName ?? '',
        partnerPlatformNameMap[r.platform],
        r.amount,
      ]);

      const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);

      // Apply header styles (row 0)
      headers.forEach((_, colIndex) => {
        const cellRef = XLSX.utils.encode_cell({ r: 0, c: colIndex });
        ws[cellRef].s = headerStyle;
      });

      // Apply cell borders to data rows
      data.forEach((row, rowIndex) => {
        row.forEach((_, colIndex) => {
          const cellRef = XLSX.utils.encode_cell({
            r: rowIndex + 1,
            c: colIndex,
          });
          if (ws[cellRef]) ws[cellRef].s = cellStyle;
        });
      });

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Rapor');

      XLSX.writeFile(wb, 'urun-satis-raporu.xlsx');
    } catch (error: any) {
      toast.error(
        error.message || 'Bir hata oluştu. Lütfen daha sonra tekrar deneyiniz.',
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshReport();
  }, []);

  const handleSearch = () => {
    const newFilter = {
      ...filter,
      pagination: { ...filter.pagination!, page: 0 },
    };

    setFilter(newFilter);
    refreshReport(newFilter);
  };

  const handlePagination = (p: Pagination) => {
    const newFilter = { ...filter, pagination: p };

    setFilter(newFilter);
    refreshReport(newFilter);
  };

  return (
    <Box sx={{ ...sharedStyles.pageBody }}>
      <Waiting open={loading} />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          mb: 1.5,
        }}
      >
        <Typography variant='h5' sx={{ ...sharedStyles.pageTitle, my: 'auto' }}>
          Ürün Satış Raporu
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            ml: 'auto',
          }}
        >
          <FormatListBulletedIcon
            color={view === 'table' ? 'primary' : 'inherit'}
          />

          <Switch
            checked={view === 'chart'}
            onChange={() => setView(view === 'table' ? 'chart' : 'table')}
          />

          <BarChartIcon color={view === 'chart' ? 'primary' : 'inherit'} />
        </Box>
      </Box>

      <Paper sx={{ mb: 1.5, p: 1.5 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 1.5,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <Box sx={dateTimePickerStyle}>
            <BrandDropdown
              label='Marka'
              brandID={filter.brandID}
              onChange={(x) => setFilter({ ...filter, brandID: x })}
            />
          </Box>

          <Box sx={dateTimePickerStyle}>
            <ProductsDropdown
              label='Ürün'
              multiple={false}
              value={filter.productID}
              onChange={(x) => setFilter({ ...filter, productID: x })}
            />
          </Box>

          <Box sx={dateTimePickerStyle}>
            <PartnerPlatformDropdown
              label='Platform'
              value={filter.platform}
              onChange={(x) => setFilter({ ...filter, platform: x })}
            />
          </Box>

          <Box sx={dateTimePickerStyle}>
            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              locale={trLocale}
            >
              <DateTimePicker
                label='Başlangıç'
                onChange={(newStartAt) =>
                  setFilter({ ...filter, startAt: newStartAt ?? undefined })
                }
                value={filter.startAt ?? null}
                inputFormat='d MMMM yyyy HH:mm'
                renderInput={(params) => (
                  <TextField {...params} size='small' fullWidth />
                )}
              />
            </LocalizationProvider>
          </Box>

          <Box sx={dateTimePickerStyle}>
            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              locale={trLocale}
            >
              <DateTimePicker
                label='Bitiş'
                onChange={(newEndAt) =>
                  setFilter({ ...filter, endAt: newEndAt ?? undefined })
                }
                value={filter.endAt ?? null}
                inputFormat='d MMMM yyyy HH:mm'
                renderInput={(params) => (
                  <TextField {...params} size='small' fullWidth />
                )}
              />
            </LocalizationProvider>
          </Box>

          <PeriodDropdown
            label='Periyot'
            value={filter.period}
            onChange={(newPeriod) =>
              setFilter({ ...filter, period: newPeriod })
            }
          />

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              flexGrow: 1,
              gap: 1.5,
              justifyContent: 'end',
            }}
          >
            <Button
              variant='contained'
              startIcon={<DeleteIcon />}
              color='error'
              onClick={() =>
                setFilter({
                  ...getDefaultProductSalesReportFilter(),
                  pagination: filter.pagination,
                })
              }
              sx={{ ...sharedStyles.buttonText }}
            >
              Filtreyi Sıfırla
            </Button>

            <Button
              variant='contained'
              startIcon={<DownloadIcon />}
              onClick={downloadReport}
              sx={{ ...sharedStyles.buttonText }}
            >
              İndir
            </Button>

            <Button
              variant='contained'
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              sx={{ ...sharedStyles.buttonText }}
            >
              Filtrele
            </Button>
          </Box>
        </Box>
      </Paper>

      {view === 'table' ? (
        <ProductSalesReportTable
          rows={report.data}
          period={appliedPeriod}
          totalCount={report.totalCount}
          pagination={filter.pagination!}
          onPagination={handlePagination}
        />
      ) : (
        <ProductSalesChart filters={filter} />
      )}
    </Box>
  );
};
